import React, { createContext, useCallback, useContext, useState, useEffect, useRef } from 'react'
import { ROUTES } from './routes'
import { isChangePlanPath, isPricingPath } from './checkout/pricingPath'
import { isOfferPath } from './downsellPath'
import { isPaymentPath } from './checkout/pendingCheckout'
import { isProductPath, isWaitlistPath } from './products/paths'
import { isInviteDeclinePath } from './inviteDeclinePath'
import { isEmailUnsubscribePath } from './emailUnsubscribePath'
import { isAccountErasureCancelPath } from './erasure'
import { isSurveyPath } from './surveyPaths'
import { useOptionalCommunity } from './CommunityContext'
import { communityNavigation } from './navigation'

/**
 * F-191 phase 2: on screens from 1024 px the side menu is either full (names)
 * or a rail (icons only). The community sets the starting state
 * (`appearance.navigation.sidebarDefault`, read only here); the member's own
 * choice, from the header button or Minha Conta › Aparência, stays in this
 * browser, per community. Below 1024 px the menu is always the drawer.
 */
export type SidebarMode = 'full' | 'rail'
/** What the member picked: the community's default or a fixed state. */
export type MemberSidebarMode = 'community' | SidebarMode

export const MEMBER_SIDEBAR_MODES: MemberSidebarMode[] = ['community', 'full', 'rail']
export const DESKTOP_QUERY = '(min-width: 1024px)'

const isSidebarMode = (value: unknown): value is SidebarMode => value === 'full' || value === 'rail'

/** The community's starting state, `full` for an older API or a missing key. */
export const communitySidebarDefault = (appearance: unknown): SidebarMode => {
  const navigation = (appearance && typeof appearance === 'object' ? (appearance as { navigation?: unknown }).navigation : null) as { sidebarDefault?: unknown } | null
  return isSidebarMode(navigation?.sidebarDefault) ? navigation.sidebarDefault : 'full'
}

const storageKey = (communityId: string | undefined) => `memberfy:sidebar:${communityId || 'none'}`

const readMemberMode = (communityId: string | undefined): MemberSidebarMode => {
  try {
    const value = window.localStorage.getItem(storageKey(communityId))
    return isSidebarMode(value) ? value : 'community'
  } catch {
    return 'community'
  }
}

const writeMemberMode = (communityId: string | undefined, value: MemberSidebarMode) => {
  try {
    if (value === 'community') window.localStorage.removeItem(storageKey(communityId))
    else window.localStorage.setItem(storageKey(communityId), value)
  } catch {
    /* Private window or blocked storage: the choice lasts until the tab closes. */
  }
}

const matchesDesktop = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(DESKTOP_QUERY).matches

interface SidebarContextType {
  isOpen: boolean
  toggle: () => void
  close: () => void
  isCollapsed: boolean
  setCollapsed: (collapsed: boolean) => void
  toggleCollapsed: () => void
  /** 1024 px and up: the full menu or the rail; below, the drawer. */
  isDesktop: boolean
  /** The state the menu shows on desktop when it is not hidden by the page. */
  mode: SidebarMode
  /** The member's own choice (`community` = follow the community's default). */
  memberMode: MemberSidebarMode
  setMemberMode: (value: MemberSidebarMode) => void
  /** The rail is on screen now (desktop, rail mode, not hidden by the page). */
  isRail: boolean
  /**
   * F-191 phase 5: the community picked the bar on top (sections in the
   * header from 1024 px, the section strip below it on phones). False while
   * an owner or admin edits the menu, which needs the side menu.
   */
  isTopNav: boolean
  /** The community's layout is the top bar (whether or not the menu is being edited). */
  topNavConfigured: boolean
  /** "Editar menu" from the top bar: the side menu comes back, for this tab, until "Concluir". */
  menuEditing: boolean
  setMenuEditing: (editing: boolean) => void
}

/**
 * Pages that open with the menu collapsed: pricing, checkout, offers, set
 * password and the AI assistant's consent screen (whose path carries the
 * OAuth query).
 */
const startsCollapsed = (currentPath = '') => {
  const path = currentPath.split('?')[0]
  return isPricingPath(currentPath) || isChangePlanPath(currentPath) || isOfferPath(currentPath) || isPaymentPath(currentPath)
    || isProductPath(currentPath) || isWaitlistPath(currentPath) || isInviteDeclinePath(currentPath) || isEmailUnsubscribePath(currentPath) || isAccountErasureCancelPath(currentPath) || isSurveyPath(currentPath)
    || path === '/products' || path === ROUTES.authSetPassword || path === ROUTES.supportEntry || path === ROUTES.mcpAuthorize
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined)

interface SidebarProviderProps {
  children: React.ReactNode
  currentPath?: string
  autoCollapsed?: boolean
}

export function SidebarProvider({ children, currentPath, autoCollapsed = false }: SidebarProviderProps) {
  const isPublicRoute = startsCollapsed(currentPath)
  const [isOpen, setIsOpen] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(isPublicRoute || autoCollapsed)

  /* The menu as the person left it before a page that collapses it; back to it on the way out. */
  const collapsedBefore = useRef<boolean | null>(isPublicRoute ? false : null)
  const collapsedNow = useRef(isCollapsed)
  collapsedNow.current = isCollapsed

  useEffect(() => {
    const isPublic = startsCollapsed(currentPath)
    if (isPublic) {
      if (collapsedBefore.current === null) collapsedBefore.current = collapsedNow.current
      setIsCollapsed(true)
    } else {
      setIsCollapsed(autoCollapsed || (collapsedBefore.current ?? false))
      collapsedBefore.current = null
    }
    if (isPublic || autoCollapsed) {
      setIsOpen(false)
    }
  }, [currentPath, autoCollapsed])

  const toggle = () => setIsOpen(!isOpen)
  /* Stable, so effects can depend on it without re-running every render. */
  const close = useCallback(() => setIsOpen(false), [])
  const toggleCollapsed = () => setIsCollapsed(!isCollapsed)

  const community = useOptionalCommunity()
  const communityId = community?.id
  const [memberMode, setMemberModeState] = useState<MemberSidebarMode>(() => readMemberMode(communityId))
  useEffect(() => { setMemberModeState(readMemberMode(communityId)) }, [communityId])
  const setMemberMode = useCallback((value: MemberSidebarMode) => {
    setMemberModeState(value)
    writeMemberMode(communityId, value)
  }, [communityId])
  const mode: SidebarMode = memberMode === 'community' ? communitySidebarDefault(community?.appearance) : memberMode

  const [isDesktop, setIsDesktop] = useState(matchesDesktop)
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const query = window.matchMedia(DESKTOP_QUERY)
    const onChange = () => setIsDesktop(query.matches)
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])

  /* `--sidebar-width` becomes the rail's width from 1024 px (index.css), so the
     page's margin and anything placed beside the menu follow it. */
  useEffect(() => {
    document.documentElement.classList.toggle('sidebar-rail', mode === 'rail')
    return () => { document.documentElement.classList.remove('sidebar-rail') }
  }, [mode])

  const topNavConfigured = communityNavigation(community?.appearance).layout === 'top'
  const [menuEditing, setMenuEditing] = useState(false)
  const isTopNav = topNavConfigured && !menuEditing

  /* No side menu at all with the top bar: the page takes the full width (index.css). */
  useEffect(() => {
    document.documentElement.classList.toggle('nav-top', isTopNav)
    if (isTopNav) setIsOpen(false)
    return () => { document.documentElement.classList.remove('nav-top') }
  }, [isTopNav])

  const isRail = isDesktop && mode === 'rail' && !isCollapsed && !isTopNav

  return (
    <SidebarContext.Provider value={{
      isOpen,
      toggle,
      close,
      isCollapsed,
      setCollapsed: setIsCollapsed,
      toggleCollapsed,
      isDesktop,
      mode,
      memberMode,
      setMemberMode,
      isRail,
      isTopNav,
      topNavConfigured,
      menuEditing,
      setMenuEditing
    }}>
      {children}
    </SidebarContext.Provider>
  )
}

export function useSidebar() {
  const context = useContext(SidebarContext)
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider')
  }
  return context
}